/**
 * «این درخواست چه شکلی داشت؟» — بدونِ اینکه بگوید دنبالِ چه کسی بود (WHOSUP-325).
 *
 * لاگِ فعالیت برای هر `events.select` فقط زمان و تعدادِ ردیف داشت؛ کندترین
 * موردها `detail: {}` بودند و نمی‌شد فهمید کدام فیلتر کند است. این‌جا از
 * آدرسِ درخواستی که سازنده‌ی کوئریِ سوپابیس (PostgREST) ساخته، فقط **شکل**
 * بیرون کشیده می‌شود:
 *
 *   - فیلترها به صورتِ `ستون:نوعِ‌شرط` (`city:eq`, `starts_at:gte`) — مقدار هرگز.
 *     برای `ilike`/`like`/`fts`… که مقدارشان متنِ جست‌وجوی کاربر است، فقط
 *     طولِ آن متن (`title:ilike#5`).
 *   - `or`/`and` فقط با تعدادِ شرط‌های داخلشان، چون داخلشان مقدار است.
 *   - ستون‌های خواسته‌شده (کوتاه‌شده) یا `*`، و `limit`/`offset`/`order`.
 *   - برای `rpc` فقط **نامِ** آرگومان‌ها، نه مقدارشان.
 *
 * تابعِ خالص است تا با `npm test` آزموده شود؛ سقفِ اندازه را `trimDetail` می‌گذارد.
 */

/** شرط‌هایی که مقدارشان معمولاً متنی است که کاربر تایپ کرده. */
const TEXT_OPS = new Set(['like', 'ilike', 'match', 'imatch', 'fts', 'plfts', 'phfts', 'wfts']);

/** پارامترهایی که فیلتر نیستند. */
const RESERVED = new Set(['select', 'order', 'limit', 'offset', 'on_conflict', 'columns']);

const MAX_COLS = 160;

/** تعدادِ شرط‌های سطحِ اولِ یک `(a.eq.1,b.gt.2)` — کاماهای داخلِ پرانتزِ تودرتو شمرده نمی‌شوند. */
function countTerms(group: string): number {
  const inner = group.replace(/^\(/, '').replace(/\)$/, '');
  if (!inner) return 0;
  let depth = 0;
  let n = 1;
  for (const ch of inner) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    else if (ch === ',' && depth === 0) n++;
  }
  return n;
}

function filterShape(col: string, value: string): string {
  if (col === 'or' || col === 'and' || col.endsWith('.or') || col.endsWith('.and')) {
    return `${col}(${countTerms(value)})`;
  }
  const parts = value.split('.');
  let i = 0;
  let op = parts[i++] ?? '';
  if (op === 'not') op = `not.${parts[i++] ?? ''}`;
  const bare = op.replace(/^not\./, '').replace(/\(.*$/, '');
  if (TEXT_OPS.has(bare)) {
    const text = parts.slice(i).join('.');
    return `${col}:${op}#${text.length}`;
  }
  if (bare === 'in') return `${col}:${op}(${countTerms(parts.slice(i).join('.'))})`;
  return `${col}:${op}`;
}

/** فهرستِ ستون‌های `select` بدونِ فاصله، و کوتاه‌شده اگر خیلی بلند است. */
function colsShape(select: string): string {
  const s = select.replace(/\s+/g, '');
  return s.length > MAX_COLS ? `${s.slice(0, MAX_COLS)}…(${s.length})` : s;
}

/**
 * شکلِ یک درخواست از روی آدرس (و برای `rpc` بدنه‌اش).
 * هر چیزی که نشود فهمید، فقط نیامده — این تابع هرگز پرتاب نمی‌کند.
 */
export function queryShape(url: unknown, body?: unknown, isRpc = false): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  try {
    let params: URLSearchParams | null = null;
    if (url instanceof URL) params = url.searchParams;
    else if (typeof url === 'string') params = new URL(url, 'http://x').searchParams;
    if (params) {
      const filters: string[] = [];
      for (const [k, v] of params) {
        if (!RESERVED.has(k)) filters.push(filterShape(k, v));
      }
      if (filters.length) out.filters = filters.join(',');
      const select = params.get('select');
      if (select) out.cols = colsShape(select);
      const limit = Number(params.get('limit'));
      if (params.has('limit') && Number.isFinite(limit)) out.limit = limit;
      const offset = Number(params.get('offset'));
      if (params.has('offset') && Number.isFinite(offset)) out.offset = offset;
      const order = params.get('order');
      if (order) out.order = order;
    }
    if (isRpc && body && typeof body === 'object' && !Array.isArray(body)) {
      const keys = Object.keys(body);
      if (keys.length) out.args = keys.join(',');
    }
  } catch { /* شکل نیامد؛ بقیه‌ی سطر هنوز ارزش دارد */ }
  return out;
}
